import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { Order } from "@/api/types";
import dayjs from "dayjs";

Font.register({
  family: "Montserrat",
  fonts: [
    {
      src: "/fonts/montserrat.ttf", // regular
      fontWeight: "normal",
    },
    {
      src: "/fonts/montserrat-bold.ttf", // bold
      fontWeight: "bold",
    },
  ],
});

const Receipt = ({ order }: { order: Order }) => {
  //HR VAT amount 25%, 1.25
  const totalWithoutVAT = (Number(order.price) / 1.25).toFixed(2);
  const vatAmount = (Number(order.price) - Number(totalWithoutVAT)).toFixed(2);
  const calculatePriceWithoutVat = (price: string) => {
    return (Number(price) / 1.25).toFixed(2);
  };
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.wrapper}>
          <View style={styles.content}>
            <View style={styles.header}>
              <View style={styles.imageContainer}>
                <Image src="/images/logo.png" style={styles.image} />
              </View>
              <View style={styles.headerContent}>
                <Text style={styles.invoiceNumber}>
                  Narudžba #{order?.invoiceNumber}
                </Text>
                <Text style={styles.headerInfo}>
                  Datum računa: {dayjs(order?.createdAt).format("DD.MM.YYYY.")}{" "}
                  u {dayjs(order?.createdAt).format("hh:mm")}
                </Text>
                <Text style={styles.headerInfo}>
                  https://drevija.gnkdinamo.hr/
                </Text>
              </View>
            </View>
            <View style={styles.detailsContainer}>
              <View style={styles.partyDetails}>
                <Text style={{ fontSize: 9 }}>Račun izdao:</Text>
                <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                  CoreEvent d.o.o.
                </Text>
                <Text style={{ fontSize: 9 }}>
                  Dunjevac 15, 10000 Zagreb Hrvatska
                </Text>
                <Text style={{ fontSize: 8 }}>OIB: 3661133536</Text>
              </View>

              <View style={styles.partyDetails}>
                <Text style={{ fontSize: 9 }}>Kupac:</Text>
                <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                  {order?.metadata?.customer?.customerFirstName}{" "}
                  {order?.metadata?.customer?.customerSurname}
                </Text>
                <Text style={{ fontSize: 9 }}>
                  {order?.metadata?.customer?.customerAddress},{" "}
                  {order?.metadata?.customer?.customerZIP}
                  {", "}
                  {order?.metadata?.customer?.customerCity}
                  {", "}
                  {order?.metadata?.customer?.customerCountry}
                </Text>
                <Text style={{ fontSize: 8 }}>
                  {order?.metadata?.customer?.customerEmail}
                </Text>
              </View>
            </View>
            <View style={styles.itemsSection}>
              <View style={styles.tableRow}>
                <Text style={[styles.cell, { flex: 3, fontWeight: "bold" }]}>
                  Artikl
                </Text>
                <Text
                  style={[
                    styles.cell,
                    { flex: 1, textAlign: "center", fontWeight: "bold" },
                  ]}
                >
                  Jed.
                </Text>
                <Text
                  style={[
                    styles.cell,
                    { flex: 1, textAlign: "center", fontWeight: "bold" },
                  ]}
                >
                  Kol.
                </Text>
                <Text
                  style={[
                    styles.cell,
                    { flex: 1, textAlign: "right", fontWeight: "bold" },
                  ]}
                >
                  Cijena
                </Text>
                <Text
                  style={[
                    styles.cell,
                    { flex: 1, textAlign: "center", fontWeight: "bold" },
                  ]}
                >
                  Porez
                </Text>
                <Text
                  style={[
                    styles.cell,
                    { flex: 1, textAlign: "right", fontWeight: "bold" },
                  ]}
                >
                  Iznos stavke
                </Text>
              </View>

              {order?.orderItems?.map((item) => {
                const title =
                  item.subscription?.title ||
                  item.issue?.title ||
                  item.publication?.title;

                return (
                  <View style={styles.tableRow} key={item.id}>
                    <Text style={[styles.cell, { flex: 3 }]}>{title}</Text>
                    <Text
                      style={[styles.cell, { flex: 1, textAlign: "center" }]}
                    >
                      kom
                    </Text>
                    <Text
                      style={[styles.cell, { flex: 1, textAlign: "center" }]}
                    >
                      1
                    </Text>
                    <Text
                      style={[styles.cell, { flex: 1, textAlign: "right" }]}
                    >
                      {calculatePriceWithoutVat(item.price)} €
                    </Text>
                    <Text
                      style={[styles.cell, { flex: 1, textAlign: "center" }]}
                    >
                      25%
                    </Text>
                    <Text
                      style={[styles.cell, { flex: 1, textAlign: "right" }]}
                    >
                      {item.price} €
                    </Text>
                  </View>
                );
              })}
            </View>
            <View style={{ alignItems: "flex-end", marginTop: 8 }}>
              <View style={styles.totalRow}>
                <Text style={{ fontSize: 9 }}>Ukupno bez poreza:</Text>
                <Text style={{ fontSize: 9 }}>{totalWithoutVAT} €</Text>
              </View>
            </View>
            <View style={{ alignItems: "flex-end", marginTop: 4 }}>
              <View style={styles.totalRow}>
                <Text style={{ fontSize: 9 }}>
                  Porez (25%), osnovice {totalWithoutVAT} €:
                </Text>
                <Text style={{ fontSize: 9 }}>{vatAmount} €</Text>
              </View>
            </View>

            <View style={{ alignItems: "flex-end", marginTop: 4 }}>
              <View style={styles.totalRow}>
                <Text style={{ fontSize: 9 }}>Ukupno:</Text>
                <Text style={{ fontSize: 9, fontWeight: "bold" }}>
                  {order?.price} €
                </Text>
              </View>
            </View>
            <View style={styles.paymentType}>
              <Text style={{ fontSize: 8 }}>Način plaćanja:</Text>
              <Text style={{ fontSize: 9 }}>Kartično plaćanje</Text>
            </View>
          </View>

          <View style={styles.footerContainer}>
            <View style={styles.footerColumn}>
              <Text style={styles.footerHeading}>Naše adrese</Text>
              <Text style={styles.footerText}>
                Dunjevac 15, 10000 Zagreb, Hrvatska
              </Text>
              <Text style={styles.footerText}>
                Internet: https://core-event.co/
              </Text>
            </View>

            <View style={styles.footerColumn}>
              <Text style={styles.footerHeading}>Pravni podaci</Text>
              <Text style={styles.footerText}>OIB: 36611335369</Text>
              <Text style={styles.footerText}>IBAN: HR5223400091111141277</Text>
              <Text style={styles.footerText}>SWIFT: PBZGHR2X</Text>
            </View>

            <View style={styles.footerColumn}>
              <Text style={styles.footerHeading}>Kontakti</Text>
              <Text style={styles.footerText}>
                E-mail: support@core-event.co
              </Text>
            </View>
          </View>
          <View style={styles.footerBottom}>
            <Text style={styles.footerText}>
              Društvo je upisano u sudski registar Trgovačkog suda u Zagrebu pod
              brojem 081377997. Temeljni kapital društva iznosi 2.800,00 EUR i
              uplaćen je u cijelosti. Račun za redovno poslovanje društva vodi
              se kod Privredne Banke Zagreb.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default Receipt;

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 11,
    fontFamily: "Montserrat",
  },
  wrapper: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  content: {
    flexGrow: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  imageContainer: {
    padding: 6,
    alignSelf: "flex-start",
    flexDirection: "column",
    marginTop: 0,
  },
  image: {
    height: 32,
    width: 32,
    objectFit: "contain",
    alignSelf: "flex-start",
  },
  headerContent: {
    alignContent: "flex-end",
    textAlign: "right",
    width: 300,
  },
  invoiceNumber: {
    fontSize: 8,
    marginBottom: 5,
    textAlign: "right",
    alignItems: "flex-end",
    fontWeight: "bold",
  },
  headerInfo: {
    fontSize: 8,
    marginBottom: 5,
    textAlign: "right",
  },
  detailsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 40,
  },
  partyDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  itemsSection: {
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #ccc",
    paddingVertical: 4,
  },
  cell: {
    fontSize: 9,
    paddingHorizontal: 2,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
  },
  paymentType: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginBottom: 15,
  },
  footerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30,
    marginBottom: 10,
  },
  footerColumn: {
    flexDirection: "column",
    flex: 1,
    paddingRight: 10,
  },
  footerHeading: {
    fontSize: 6,
    fontWeight: "bold",
    marginBottom: 2,
  },
  footerText: {
    fontSize: 6,
    marginBottom: 2,
  },
  footerBottom: {
    paddingTop: 5,
  },
});
